import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow } from './stylesFixtureData';

/**
 * Dedicated, real persisted `stylesData` fixtures for the DL-66466 epic ("Styles bulk edit —
 * selection gating, non-editable states, limits and permissions", Testmo folder 93165 part 3 of 4
 * — dl-58397). Real rows (not an `apiTransform`-only override) so the bulk-edit selection-gating
 * flow — non-editable-state removal, the pre-open warning modal, and the actual bulk PUT — runs
 * through the same find-by-id/merge/persist path as every other row. That is what makes the
 * "the removed style's id is NEVER in the outgoing payload" / "no bulk request was sent"
 * assertions real: an `apiTransform`-injected row would bypass the handler and make any outcome
 * look correct.
 *
 * Every row is searchable by a unique `SelectionGating <Group>` name substring absent from every
 * other fixture in this mock, so each spec isolates exactly its own rows via `?search=<token>`.
 * States use the lowercase wire values (`STYLE_STATES` in `styles.ts`); `nonEditableStates`
 * (`validation/styleState.ts`) is `['delivered', 'cancelled', 'deleted']`.
 *
 * id range 964xx — above every existing styles fixture (90300-90361 validation/probe, 95001-95132
 * clear-field/set2/actions, 95850-95851 dl58912) and the generated 1-150 pool, so a bulk PUT can
 * never `findIndex` onto the wrong row.
 *
 * Split out of `styles.ts` purely to keep that file under the repo's 800-line cap — same pattern
 * as `stylesBulkActionsFixtureData.ts` / `stylesBulkEditProbeFixtureData.ts`.
 */

const baseRow = (id: number, name: string, overrides: Partial<StyleRow> = {}): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: '',
  // Editable default. AW26 (seasons[1]) is a genuine bulk-edit Season change; SS25 (seasons[2])
  // is the seeded starting value (mirrors stylesBulkActionsFixtureData.ts's baseRow).
  state: 'unpublished',
  logo: '',
  season: seasons[2],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * DL-66467 / Testmo #754726 — a Deleted style in a MIXED selection is removed after the user
 * continues; only the editable style is edited and its id alone reaches the bulk PUT. One editable
 * (Unpublished) + one Deleted row.
 */
export const selectionGatingMixedDeletedRows: StyleRow[] = [
  baseRow(96401, 'SelectionGating MixedDeleted Editable'),
  baseRow(96402, 'SelectionGating MixedDeleted Deleted', { state: 'deleted' }),
];
export const MIXED_DELETED_EDITABLE_ID = 96401;
export const MIXED_DELETED_DELETED_ID = 96402;

/**
 * DL-66468 / Testmo #754727 — when ALL selected styles are non-editable the warning modal shows
 * "None of the selected styles can be edited", the edit modal never opens, and no bulk request is
 * sent. Two non-editable rows (Cancelled + Deleted), no editable row.
 */
export const selectionGatingAllNonEditableRows: StyleRow[] = [
  baseRow(96403, 'SelectionGating AllNonEditable Cancelled', { state: 'cancelled' }),
  baseRow(96404, 'SelectionGating AllNonEditable Deleted', { state: 'deleted' }),
];

/**
 * DL-66469 / Testmo #754745 — the pre-open warning modal lists every removed style by name AND
 * number (customId). One editable + two non-editable of DISTINCT states (Cancelled + Delivered) so
 * the removed list carries two entries.
 */
export const selectionGatingRemovedListRows: StyleRow[] = [
  baseRow(96405, 'SelectionGating RemovedList Editable'),
  baseRow(96406, 'SelectionGating RemovedList Cancelled', { state: 'cancelled' }),
  baseRow(96407, 'SelectionGating RemovedList Delivered', { state: 'delivered' }),
];
export const REMOVED_LIST_EDITABLE_ID = 96405;

/** DL-66472 / Testmo #754738 — Escape clears a built selection (modal closed). Editable rows. */
export const selectionGatingEscClearRows: StyleRow[] = [
  baseRow(96410, 'SelectionGating EscClear A'),
  baseRow(96411, 'SelectionGating EscClear B'),
];

/** DL-66473 / Testmo #754739 — the floating bulk-bar X button clears a built selection. */
export const selectionGatingXClearRows: StyleRow[] = [
  baseRow(96412, 'SelectionGating XClear A'),
  baseRow(96413, 'SelectionGating XClear B'),
];

/** DL-66474 / Testmo #754723 — Next is disabled until a field is changed. */
export const selectionGatingNextGateRows: StyleRow[] = [
  baseRow(96415, 'SelectionGating NextGate A'),
  baseRow(96416, 'SelectionGating NextGate B'),
];

/** DL-66475 / Testmo #754724 — Cancel closes the modal without applying, selection preserved. */
export const selectionGatingCancelRows: StyleRow[] = [
  baseRow(96417, 'SelectionGating Cancel A'),
  baseRow(96418, 'SelectionGating Cancel B'),
];

/** DL-66476 / Testmo #754725 — the modal's X close button behaves identically to Cancel. */
export const selectionGatingModalXRows: StyleRow[] = [
  baseRow(96419, 'SelectionGating ModalX A'),
  baseRow(96420, 'SelectionGating ModalX B'),
];

/** DL-66470 / Testmo #754740 — a role-less user cannot reach bulk edit. Plain editable rows. */
export const selectionGatingPermissionRows: StyleRow[] = [
  baseRow(96421, 'SelectionGating Perm A'),
  baseRow(96422, 'SelectionGating Perm B'),
  baseRow(96423, 'SelectionGating Perm C'),
];

/** Pushed as a block into `stylesData` by `styles.ts` on a fresh (non-restored) seed. */
export const stylesSelectionGatingFixtures: StyleRow[] = [
  ...selectionGatingMixedDeletedRows,
  ...selectionGatingAllNonEditableRows,
  ...selectionGatingRemovedListRows,
  ...selectionGatingEscClearRows,
  ...selectionGatingXClearRows,
  ...selectionGatingNextGateRows,
  ...selectionGatingCancelRows,
  ...selectionGatingModalXRows,
  ...selectionGatingPermissionRows,
];
